import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, authedFileUrl, errMsg } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, Download, Printer, Search } from "lucide-react";
import { CustomFieldsInputs } from "../components/CustomFields";

const emptyTrip = {
  trip_number: "", origin_city: "", origin_state: "", destination_city: "", destination_state: "",
  paid_miles: "", gross_amount: "", load_date: new Date().toISOString().slice(0, 10),
  is_team: false, driver_ids: [""], deductions: [],
  fuel_surcharge_rate_pct: "", fuel_surcharge: "",
  fuel_expense: "", other_expense: "", back_office_percent: "", notes: "",
};

// Safe UUID generator — crypto.randomUUID() is undefined in some mobile Safari
// contexts (older iOS, in-app browsers), which was silently killing the
// "Custom" deduction button. This fallback always works.
const rid = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `rid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export default function Trips() {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyTrip);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // When the user manually types the Gross field we stop auto-calculating it.
  // Cleared when a new/edit trip dialog opens.
  const grossManual = useRef(false);

  const [query, setQuery] = useState("");
  const [driverFilter, setDriverFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () =>
    Promise.all([
      api.get("/trips").then((r) => setTrips(r.data)),
      api.get("/drivers").then((r) => setDrivers(r.data)),
      api.get("/deduction-templates").then((r) => setTemplates(r.data)),
    ]).catch(() => {});
  useEffect(() => { load(); }, []);

  const driver = (id) => drivers.find((d) => d.driver_id === id);
  const driverName = (id) => driver(id)?.name || "—";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return trips.filter((t) => {
      if (driverFilter !== "all" && !t.driver_ids.includes(driverFilter)) return false;
      if (modeFilter !== "all") {
        const d = driver(t.driver_ids[0]);
        if (!d || d.pay_type !== modeFilter) return false;
      }
      if (dateFrom && t.load_date < dateFrom) return false;
      if (dateTo && t.load_date > dateTo) return false;
      if (!q) return true;
      return (
        t.trip_number.toLowerCase().includes(q) ||
        `${t.origin_city}${t.origin_state}${t.destination_city}${t.destination_state}`.toLowerCase().includes(q)
      );
    });
  }, [trips, drivers, query, driverFilter, modeFilter, dateFrom, dateTo]);

  const totals = useMemo(() => {
    let miles = 0, gross = 0, pay = 0;
    filtered.forEach((t) => {
      const lead = driver(t.driver_ids[0]);
      const m = parseFloat(t.paid_miles) || 0;
      const g = parseFloat(t.gross_amount) || 0;
      miles += m;
      gross += g;
      if (lead) {
        const factor = 1; // Team drivers earn full miles x lead_rate (no halving)
        if (lead.pay_type === "per_mile") pay += m * factor * lead.pay_rate;
        else pay += g * factor * (lead.pay_rate / 100);
      }
    });
    return { miles, gross, pay };
  }, [filtered, drivers]);

  const openNew = () => { grossManual.current = false; setForm(emptyTrip); setEditing(null); setOpen(true); };

  // Default base rate per mile when no driver is picked yet, so the Gross
  // Amount can auto-populate the moment miles and fuel surcharge are entered.
  const DEFAULT_BASE_RATE = 3.05;

  // Auto-populate Gross Amount from miles × base rate per mile, plus the fuel
  // surcharge (rate × miles if a per-mile rate is given, else the flat fuel
  // surcharge). Uses the picked driver's base rate once a driver is selected,
  // otherwise falls back to DEFAULT_BASE_RATE so the box fills in as soon as
  // miles + fuel are entered. Stops auto-filling once the user types manually.
  useEffect(() => {
    if (!open || grossManual.current) return;
    const miles = parseFloat(form.paid_miles) || 0;
    const lead = drivers.find((d) => d.driver_id === form.driver_ids[0]);
    const base = (lead && parseFloat(lead.base_rate_per_mile)) || DEFAULT_BASE_RATE;
    const fsrPct = parseFloat(form.fuel_surcharge_rate_pct) || 0;
    const fsr = fsrPct / 100;
    const flatFs = parseFloat(form.fuel_surcharge) || 0;
    const fuel = fsr > 0 ? miles * fsr : flatFs;
    const auto = miles * base + fuel;
    const current = parseFloat(form.gross_amount) || 0;
    if (Math.abs(auto - current) > 0.005) {
      setForm((f) => ({ ...f, gross_amount: auto ? auto.toFixed(2) : "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.paid_miles, form.fuel_surcharge_rate_pct, form.fuel_surcharge, form.driver_ids[0], drivers]);

  const toggleTeam = (val) => {
    setForm((f) => ({
      ...f, is_team: val,
      driver_ids: val ? (f.driver_ids.length === 2 ? f.driver_ids : [f.driver_ids[0] || "", ""]) : [f.driver_ids[0] || ""],
    }));
  };

  const addDeductionTemplate = (templateId) => {
    const t = templates.find((x) => x.template_id === templateId);
    if (!t) return;
    setForm((f) => ({
      ...f, deductions: [...f.deductions, { _rid: rid(), label: t.label, amount: t.default_amount, kind: t.kind, note: "", percent: 100 }],
    }));
  };
  const addCustomDeduction = () =>
    setForm((f) => ({ ...f, deductions: [...f.deductions, { _rid: rid(), label: "", amount: "", kind: "deduction", note: "", percent: 100 }] }));
  const updateDeduction = (i, k, v) =>
    setForm((f) => {
      const d = [...f.deductions]; d[i] = { ...d[i], [k]: k === "amount" ? parseFloat(v) || 0 : v };
      return { ...f, deductions: d };
    });
  const removeDeduction = (i) =>
    setForm((f) => ({ ...f, deductions: f.deductions.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (form.is_team && form.driver_ids.filter(Boolean).length !== 2) { toast.error("Pick 2 drivers"); return; }
    if (!form.is_team && !form.driver_ids[0]) { toast.error("Pick a driver"); return; }
    try {
      const payload = {
        ...form,
        driver_ids: form.driver_ids.filter(Boolean),
        paid_miles: parseFloat(form.paid_miles) || 0,
        gross_amount: parseFloat(form.gross_amount) || 0,
        fuel_surcharge_rate: (parseFloat(form.fuel_surcharge_rate_pct) || 0) / 100,
        fuel_surcharge: parseFloat(form.fuel_surcharge) || 0,
        fuel_expense: parseFloat(form.fuel_expense) || 0,
        other_expense: parseFloat(form.other_expense) || 0,
        back_office_percent: form.back_office_percent === "" ? null : (parseFloat(form.back_office_percent) || 0),
        custom_values: form.custom_values || {},
      };
      if (editing) await api.put(`/trips/${editing}`, payload);
      else await api.post("/trips", payload);
      toast.success("Trip saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    await api.delete(`/trips/${id}`);
    load();
  };

  const tripPay = (t) => {
    const d = driver(t.driver_ids[0]); // team lead or sole driver
    if (!d) return 0;
    const m = parseFloat(t.paid_miles) || 0;
    const g = parseFloat(t.gross_amount) || 0;
    const factor = 1; // Team drivers earn full miles x lead_rate (no halving)
    if (d.pay_type === "per_mile") return m * factor * d.pay_rate;
    return g * factor * (d.pay_rate / 100);
  };

  const exportCSV = () => {
    const header = ["Date", "Driver(s)", "Route", "Miles", "Pay Mode", "Load #", "Gross", "Trip Pay"];
    const rows = filtered.map((t) => {
      const d = driver(t.driver_ids[0]);
      return [
        t.load_date,
        t.driver_ids.map(driverName).join(" & "),
        `${t.origin_city}, ${t.origin_state} -> ${t.destination_city}, ${t.destination_state}`,
        t.paid_miles,
        d?.pay_type === "per_mile" ? "Per Mile" : "Per %",
        t.trip_number,
        t.gross_amount,
        tripPay(t).toFixed(2),
      ];
    });
    const csv = [header, ...rows].map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "trips.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="trips-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Trips</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Every load logged against a driver, with the trip pay the pay calculator will pull for the period.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="trips-csv-btn">
            <Download size={14} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="trips-print-btn">
            <Printer size={14} className="mr-1" /> Print
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-trip-btn">
                <Plus size={16} className="mr-1.5" /> Add trips
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit" : "New"} Trip</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Trip / Load #</Label><Input data-testid="trip-number" value={form.trip_number} onChange={(e) => setForm({ ...form, trip_number: e.target.value })} /></div>
                  <div><Label>Load Date</Label><Input type="date" data-testid="trip-date" value={form.load_date} onChange={(e) => setForm({ ...form, load_date: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Origin City</Label><Input data-testid="origin-city" value={form.origin_city} onChange={(e) => setForm({ ...form, origin_city: e.target.value })} /></div>
                  <div><Label>Origin State</Label><Input data-testid="origin-state" maxLength={2} value={form.origin_state} onChange={(e) => setForm({ ...form, origin_state: e.target.value.toUpperCase() })} /></div>
                  <div><Label>Destination City</Label><Input data-testid="destination-city" value={form.destination_city} onChange={(e) => setForm({ ...form, destination_city: e.target.value })} /></div>
                  <div><Label>Destination State</Label><Input data-testid="destination-state" maxLength={2} value={form.destination_state} onChange={(e) => setForm({ ...form, destination_state: e.target.value.toUpperCase() })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Paid Miles</Label><Input type="number" data-testid="paid-miles" value={form.paid_miles} onChange={(e) => setForm({ ...form, paid_miles: e.target.value })} /></div>
                  <div><Label>Fuel Surcharge % <span className="text-xs text-slate-400">(per mile · changes weekly)</span></Label>
                    <div className="relative">
                      <Input type="number" step="1" data-testid="fuel-surcharge-rate"
                             value={form.fuel_surcharge_rate_pct}
                             onChange={(e) => setForm({ ...form, fuel_surcharge_rate_pct: e.target.value })}
                             className="pr-8" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {(parseFloat(form.paid_miles) || 0).toFixed(0)} mi × {(parseFloat(form.fuel_surcharge_rate_pct) || 0).toFixed(0)}% = ${((parseFloat(form.paid_miles) || 0) * ((parseFloat(form.fuel_surcharge_rate_pct) || 0) / 100)).toFixed(2)} fuel $
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Gross Amount ($) <span className="text-xs text-slate-400">(auto · miles × base rate + fuel)</span></Label>
                    <Input type="number" step="0.01" data-testid="gross-amount" value={form.gross_amount}
                           onChange={(e) => { grossManual.current = true; setForm({ ...form, gross_amount: e.target.value }); }} /></div>
                  <div>
                    <Label>Back Office % <span className="text-xs text-slate-400">(percentage drivers)</span></Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Select value={
                        Number(form.back_office_percent) === 35 ? "company"
                        : Number(form.back_office_percent) === 28 ? "own"
                        : "custom"
                      } onValueChange={(v) => setForm({ ...form,
                        back_office_percent: v === "company" ? 35 : v === "own" ? 28 : "",
                      })}>
                        <SelectTrigger className="w-full sm:w-44" data-testid="back-office-preset"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="company">Company trailer · 35%</SelectItem>
                          <SelectItem value="own">Own trailer · 28%</SelectItem>
                          <SelectItem value="custom">Custom %</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="relative flex-1">
                        <Input type="number" step="0.1" data-testid="back-office-percent"
                               className="text-right font-mono pr-8"
                               placeholder="Custom %"
                               value={form.back_office_percent}
                               onChange={(e) => setForm({ ...form, back_office_percent: e.target.value })} />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Back office keeps {Number(form.back_office_percent || 0).toFixed(0)}% of the line haul FIRST. The driver's own % (set on their profile) then applies to the remainder. These two cuts are independent.
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded p-3">
                  <Switch checked={form.is_team} onCheckedChange={toggleTeam} data-testid="team-toggle" />
                  <div>
                    <div className="text-sm font-semibold">Team Drivers</div>
                    <div className="text-xs text-slate-500">Both drivers earn the lead's rate on the full miles. Full load pay = 2 × each driver's pay.</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Driver {form.is_team && "#1"}</Label>
                    <Select value={form.driver_ids[0] || ""} onValueChange={(v) => {
                      const ids = [...form.driver_ids]; ids[0] = v;
                      // Pre-fill back_office_percent from the picked lead driver's
                      // stored default so the user doesn't retype it every trip.
                      const picked = drivers.find((d) => d.driver_id === v);
                      const next = { ...form, driver_ids: ids };
                      const bo = picked && picked.default_back_office_percent;
                      if (bo != null && (Number(form.back_office_percent) === 35 || !form.back_office_percent)) {
                        next.back_office_percent = bo;
                      }
                      setForm(next);
                    }}>
                      <SelectTrigger data-testid="trip-driver-1"><SelectValue placeholder="Select driver" /></SelectTrigger>
                      <SelectContent>
                        {drivers.map((d) => <SelectItem key={d.driver_id} value={d.driver_id}>{d.name} · {d.display_id}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.is_team && (
                    <div>
                      <Label>Driver #2</Label>
                      <Select value={form.driver_ids[1] || ""} onValueChange={(v) => { const ids = [...form.driver_ids]; ids[1] = v; setForm({ ...form, driver_ids: ids }); }}>
                        <SelectTrigger data-testid="trip-driver-2"><SelectValue placeholder="Second driver" /></SelectTrigger>
                        <SelectContent>
                          {drivers.filter((d) => d.driver_id !== form.driver_ids[0]).map((d) =>
                            <SelectItem key={d.driver_id} value={d.driver_id}>{d.name} · {d.display_id}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <Label>Deductions &amp; Reimbursements</Label>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Select onValueChange={addDeductionTemplate}>
                        <SelectTrigger className="flex-1 sm:w-56 h-10" data-testid="pick-template"><SelectValue placeholder="Pick from library..." /></SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.kind === "deduction" ? "−" : "+"} {t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); addCustomDeduction(); }}
                        data-testid="add-custom-deduction"
                        className="h-10 px-4 shrink-0"
                      >
                        + Custom
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {form.deductions.length === 0 && <p className="text-xs text-slate-500 italic">None yet. Pick from the library or add a custom one.</p>}
                    {form.deductions.map((d, i) => (
                      <div key={d._rid || `d-${i}`} className="rounded border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`deduction-row-${i}`}>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                          <div className="md:col-span-3">
                            <Label className="text-[11px]">Kind</Label>
                            <Select value={d.kind} onValueChange={(v) => updateDeduction(i, "kind", v)}>
                              <SelectTrigger className="h-9" data-testid={`deduction-kind-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="deduction">− Deduction</SelectItem>
                                <SelectItem value="reimbursement">+ Reimbursement</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-5">
                            <Label className="text-[11px]">Label</Label>
                            <Input placeholder="e.g. Insurance Contribution"
                                   value={d.label}
                                   data-testid={`deduction-label-${i}`}
                                   onChange={(e) => updateDeduction(i, "label", e.target.value)} />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[11px]">Amount</Label>
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                              <Input type="number" step="0.01" min="0" placeholder="0.00"
                                     className="pl-6 font-mono text-right"
                                     value={d.amount}
                                     data-testid={`deduction-amount-${i}`}
                                     onChange={(e) => updateDeduction(i, "amount", e.target.value)} />
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[11px]">Percent</Label>
                            <Select
                              value={[100, 50].includes(Number(d.percent)) ? String(Number(d.percent)) : "custom"}
                              onValueChange={(v) => {
                                if (v === "custom") updateDeduction(i, "percent", d.percent === 100 || d.percent === 50 ? 75 : d.percent);
                                else updateDeduction(i, "percent", Number(v));
                              }}
                            >
                              <SelectTrigger className="h-9" data-testid={`deduction-percent-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="100">100%</SelectItem>
                                <SelectItem value="50">50%</SelectItem>
                                <SelectItem value="custom">Custom %</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {![100, 50].includes(Number(d.percent)) && (
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                            <div className="md:col-span-10"></div>
                            <div className="md:col-span-2">
                              <Label className="text-[11px]">Custom %</Label>
                              <Input type="number" step="1" min="0" max="100"
                                     className="font-mono text-right"
                                     value={d.percent}
                                     data-testid={`deduction-custom-percent-${i}`}
                                     onChange={(e) => updateDeduction(i, "percent", Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))} />
                            </div>
                          </div>
                        )}
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-[11px]">Note / what is this for?</Label>
                            <Input placeholder="Optional note that prints on the pay statement"
                                   value={d.note || ""}
                                   data-testid={`deduction-note-${i}`}
                                   onChange={(e) => updateDeduction(i, "note", e.target.value)} />
                          </div>
                          <div className="text-right text-[11px] text-slate-500 pb-2 whitespace-nowrap">
                            Applied: <span className="font-mono font-semibold text-slate-900">
                              {d.kind === "deduction" ? "−" : "+"}${((Number(d.amount) || 0) * (Number(d.percent) || 100) / 100).toFixed(2)}
                            </span>
                          </div>
                          <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => removeDeduction(i)} data-testid={`deduction-remove-${i}`}>
                            <Trash2 size={14} className="text-rose-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Fuel Expense ($)</Label><Input type="number" step="0.01" data-testid="fuel-expense" value={form.fuel_expense} onChange={(e) => setForm({ ...form, fuel_expense: e.target.value })} /></div>
                  <div><Label>Other Expense ($)</Label><Input type="number" step="0.01" value={form.other_expense} onChange={(e) => setForm({ ...form, other_expense: e.target.value })} /></div>
                </div>
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <CustomFieldsInputs
                  scope="trip"
                  values={form.custom_values}
                  onChange={(v) => setForm({ ...form, custom_values: v })}
                />

                {/* Live totals preview */}
                {(() => {
                  const miles = parseFloat(form.paid_miles) || 0;
                  const lead = drivers.find((x) => x.driver_id === form.driver_ids[0]);
                  let grossEst = 0;
                  if (lead) {
                    if (lead.pay_type === "per_mile") {
                      grossEst = miles * (parseFloat(lead.pay_rate) || 0);
                    } else {
                      // Percentage driver with base rate:
                      //   after_bo  = line_haul × (100-BO%)/100
                      //   total_rev = after_bo + fuel
                      //   gross_est = total_rev × driver%/100   (team halves on calc page)
                      const base = parseFloat(lead.base_rate_per_mile) || 0;
                      const dpct = parseFloat(lead.pay_rate) || 40;
                      const bo = form.back_office_percent !== "" && form.back_office_percent != null
                        ? parseFloat(form.back_office_percent)
                        : (parseFloat(lead.default_back_office_percent) || 35);
                      const fsr = (parseFloat(form.fuel_surcharge_rate_pct) || 0) / 100;
                      const lineHaul = miles * base;
                      const afterBo = lineHaul * Math.max(0, (100 - bo) / 100);
                      const fuel = fsr > 0 ? miles * fsr : (parseFloat(form.fuel_surcharge) || 0);
                      grossEst = (afterBo + fuel) * dpct / 100;
                    }
                  }
                  const dedTotal = (form.deductions || [])
                    .filter((d) => d.kind === "deduction")
                    .reduce((s, d) => s + ((parseFloat(d.amount) || 0) * (parseFloat(d.percent) || 100) / 100), 0);
                  const reimTotal = (form.deductions || [])
                    .filter((d) => d.kind === "reimbursement")
                    .reduce((s, d) => s + ((parseFloat(d.amount) || 0) * (parseFloat(d.percent) || 100) / 100), 0);
                  const splitFactor = form.is_team ? 0.5 : 1;
                  const perDriverGross = grossEst * splitFactor;
                  const perDriverDed = dedTotal * splitFactor;
                  const perDriverReim = reimTotal * splitFactor;
                  const perDriverNet = perDriverGross - perDriverDed + perDriverReim;
                  return (
                    <div className="rounded border-2 border-indigo-200 bg-indigo-50 p-3" data-testid="trip-live-totals">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-900">
                        Live estimate {form.is_team && "· per driver (team split applied)"}
                      </div>
                      <div className="mt-2 grid grid-cols-4 gap-2 text-sm font-mono">
                        <div><div className="text-[10px] text-slate-500">Gross</div><div className="font-semibold">${perDriverGross.toFixed(2)}</div></div>
                        <div><div className="text-[10px] text-slate-500">− Deductions</div><div className="text-rose-700">−${perDriverDed.toFixed(2)}</div></div>
                        <div><div className="text-[10px] text-slate-500">+ Reimbursements</div><div className="text-emerald-700">+${perDriverReim.toFixed(2)}</div></div>
                        <div><div className="text-[10px] text-slate-500">Net</div><div className="font-bold text-indigo-900">${perDriverNet.toFixed(2)}</div></div>
                      </div>
                      {form.is_team && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          Load total: ${(grossEst * 2).toFixed(2)} · Deductions and reimbursements are split 50/50 across both partners.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-trip-btn">Save Trip</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter card */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">{filtered.length} trips</div>
            <div className="text-xs text-slate-500 font-mono">
              {totals.miles.toLocaleString()} mi · ${totals.gross.toFixed(2)} gross · ${totals.pay.toFixed(2)} driver pay
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search route, load #..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-8 h-9 w-56" data-testid="trips-search" />
            </div>
            <Select value={driverFilter} onValueChange={setDriverFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="trips-driver-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                {drivers.map((d) => <SelectItem key={d.driver_id} value={d.driver_id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={modeFilter} onValueChange={setModeFilter}>
              <SelectTrigger className="h-9 w-36" data-testid="trips-mode-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All pay modes</SelectItem>
                <SelectItem value="per_mile">Per Mile</SelectItem>
                <SelectItem value="per_percentage">Per %</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="trips-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-36" data-testid="trips-date-to" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5">Trip Date</th>
                <th className="px-4 py-2.5">Driver</th>
                <th className="px-4 py-2.5">Route</th>
                <th className="px-4 py-2.5 text-right">Miles</th>
                <th className="px-4 py-2.5">Pay Mode</th>
                <th className="px-4 py-2.5">Load #</th>
                <th className="px-4 py-2.5 text-right">Gross</th>
                <th className="px-4 py-2.5 text-right">Trip Pay</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center text-slate-500 py-10">No trips.</td></tr>
              )}
              {filtered.map((t) => {
                const d = driver(t.driver_ids[0]);
                return (
                  <tr key={t.trip_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`trip-row-${t.trip_number}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">{t.load_date}</td>
                    <td className="px-4 py-2.5">
                      {t.is_team && <span className="pill-amber mr-1">Team</span>}
                      <span className="font-mono text-[11px] text-slate-500">{d?.display_id} </span>
                      {t.driver_ids.map(driverName).join(" & ")}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {t.origin_city}, {t.origin_state} → {t.destination_city}, {t.destination_state}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{Number(t.paid_miles).toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      {d && <span className={d.pay_type === "per_mile" ? "pill-blue" : "pill-amber"}>
                        {d.pay_type === "per_mile" ? "Per Mile" : "Per %"}
                      </span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{t.trip_number}</td>
                    <td className="px-4 py-2.5 text-right font-mono">${Number(t.gross_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">${tripPay(t).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => {
                        // Convert stored decimal rate (0.90) back to percent (90) for the input box.
                        const fsrDecimal = parseFloat(t.fuel_surcharge_rate) || 0;
                        setForm({ ...t, fuel_surcharge_rate_pct: fsrDecimal ? String(Math.round(fsrDecimal * 10000) / 100) : "" });
                        setEditing(t.trip_id);
                        setOpen(true);
                      }}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(t.trip_id)}>
                        <Trash2 size={14} className="text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length > 0 && (
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                  <td className="px-4 py-2.5" colSpan={3}>Totals ({filtered.length} trips)</td>
                  <td className="px-4 py-2.5 text-right font-mono">{totals.miles.toLocaleString()}</td>
                  <td colSpan={2}></td>
                  <td className="px-4 py-2.5 text-right font-mono">${totals.gross.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">${totals.pay.toFixed(2)}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
